// SCR-STAFF-715 (AP-MED-05؛ US-MED-096): کارتابل پرستاری تامین — نسخه‌های پرستاری درمانگاه برای یک مراجعه‌کننده،
// به‌تفکیک پزشک، و ثبت اقدام روی ریز نسخه‌های انتخابی.
//
// صفحه فقط با نشست تامین **خود پرستار** کار می‌کند (BR-INT-108؛ PHX-MED-0335) و فقط از شعبه دارای سیام
// (PHX-MED-0333) — مطب کارتابل پرستاری ندارد. ریز نسخه‌ای که پرستار دیگری اقدام کرده (`readOnly`) انتخاب نمی‌شود.
// ثبت اقدام **ناهمگام** است: ردیف در صف می‌نشیند و نتیجه از صف می‌آید، پس UI پرستار مسدود نمی‌شود.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { isApiError, unwrap, type MedNursingCartableEntryView, type MedNursingCartableItemView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, ListPage, toJalali } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { SCR_STAFF_715, supplementaryKeys, SupplementarySampleBadge, UnsupportedNotice } from './supplementary';

/** شناسه ریز نسخه پرستاری نزد بیمه‌گر (`int64` قرارداد). */
type NursingItemId = MedNursingCartableItemView['providerItemId'];

export function NursingCartablePage() {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [params, setParams] = useSearchParams();

  const patientId = params.get('patientId') ?? '';
  const doctorNationalId = params.get('doctorNationalId') ?? '';
  // `providerItemId` در قرارداد `int64` است و مولد آن را `string | number` می‌دهد.
  const [selected, setSelected] = useState<readonly NursingItemId[]>([]);
  const tenantId = session?.tenantId;
  const canPerform = access.can('med.nursing_order.perform');

  const cartable = useQuery({
    queryKey: supplementaryKeys.cartable(tenantId, patientId, doctorNationalId),
    enabled: Boolean(tenantId) && patientId.length > 0 && canPerform,
    retry: false,
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/medical-orders/nursing-cartable', {
          params: { query: { patientId, doctorNationalId: doctorNationalId || undefined } },
        }),
      ),
  });

  const record = useMutation({
    mutationFn: (entry: MedNursingCartableEntryView) =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/nursing-cartable:record', {
          headers: { 'Idempotency-Key': idempotency.current() },
          body: {
            patientId,
            insurer: 'TAMIN',
            itemIds: [...selected],
            headId: entry.headId ?? null,
            doctorNationalId: entry.doctorNationalId ?? null,
            // نام خدمات برای ثبت در پرونده بالینی می‌رود (AC-MED-096.2)؛ در Payload رویداد نمی‌آید.
            serviceNames: entry.items.filter((item) => selected.includes(item.providerItemId)).map((item) => item.serviceName ?? '').filter((name) => name.length > 0),
          },
        }),
      ),
    onSettled: (_data, error) => {
      idempotency.settle(error ?? undefined);
      void queryClient.invalidateQueries({ queryKey: supplementaryKeys.cartable(tenantId, patientId, doctorNationalId) });
    },
    onSuccess: () => setSelected([]),
  });

  const toggle = (itemId: NursingItemId) =>
    setSelected((current) => (current.includes(itemId) ? current.filter((id) => id !== itemId) : [...current, itemId]));

  const setParam = (name: string, value: string) => {
    const next = new URLSearchParams(params);

    if (value.length > 0) {
      next.set(name, value);
    } else {
      next.delete(name);
    }

    setParams(next, { replace: true });
    setSelected([]);
  };

  // PHX-MED-0339: اتصال بیمه‌گر کارتابل پرستاری ندارد — به‌جای تلاش، علت گفته می‌شود (BR-INT-091).
  const unsupported = isApiError(cartable.error) && cartable.error.code === 'PHX-MED-0339';
  const sessionMissing = isApiError(cartable.error) && cartable.error.code === 'PHX-MED-0335';
  const siamMissing = isApiError(cartable.error) && cartable.error.code === 'PHX-MED-0333';

  return (
    <ListPage screenId={SCR_STAFF_715} titleKey="med:nursing_cartable.title" subtitleKey="med:nursing_cartable.description">
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label={t('med:nursing_cartable.patient_id')}
            value={patientId}
            onChange={(event) => setParam('patientId', event.target.value.trim())}
            slotProps={{ htmlInput: { 'data-testid': 'med-nursing-patient' } }}
            fullWidth
          />
          <TextField
            label={t('med:nursing_cartable.doctor_national_id')}
            helperText={t('med:nursing_cartable.doctor_hint')}
            value={doctorNationalId}
            onChange={(event) => setParam('doctorNationalId', event.target.value.trim())}
            slotProps={{ htmlInput: { 'data-testid': 'med-nursing-doctor' } }}
            fullWidth
          />
        </Stack>

        {patientId.length === 0 ? (
          <Alert severity="info" data-testid="med-nursing-no-patient">
            {t('med:nursing_cartable.choose_patient')}
          </Alert>
        ) : null}

        {unsupported ? <UnsupportedNotice serviceKey="nursing_cartable" /> : null}

        {sessionMissing ? (
          <Alert severity="warning" data-testid="med-nursing-session-missing">
            {t('med:nursing_cartable.session_required')}
          </Alert>
        ) : null}

        {siamMissing ? (
          <Alert severity="warning" data-testid="med-nursing-siam-missing">
            {t('med:nursing_cartable.siam_required')}
          </Alert>
        ) : null}

        {cartable.error && !unsupported && !sessionMissing && !siamMissing ? <ErrorState error={cartable.error} /> : null}

        {cartable.data?.entries.length === 0 ? (
          <Alert severity="info" data-testid="med-nursing-empty">
            {t('med:nursing_cartable.empty')}
          </Alert>
        ) : null}

        {(cartable.data?.entries ?? []).map((entry) => (
          <Stack key={entry.headId ?? entry.trackingCode ?? 'entry'} spacing={1} data-testid={`med-nursing-entry-${entry.headId ?? 'x'}`}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle2">{entry.patientName ?? '—'}</Typography>
              <Typography variant="caption" color="text.secondary">
                {entry.doctorName ?? '—'}
              </Typography>
              {entry.prescribedOn ? (
                <Typography variant="caption" color="text.secondary">
                  {toJalali(entry.prescribedOn)}
                </Typography>
              ) : null}
              <SupplementarySampleBadge isSample={cartable.data?.isSample} />
            </Stack>

            {entry.items.map((item) => (
              <FormControlLabel
                key={item.providerItemId}
                control={
                  <Checkbox
                    // `readOnly` تهی = نامعلوم؛ در آن حالت هم انتخاب بسته می‌ماند تا اقدام دوباره ثبت نشود.
                    disabled={item.isReadOnly !== false || !canPerform}
                    checked={selected.includes(item.providerItemId)}
                    onChange={() => toggle(item.providerItemId)}
                    inputProps={{ 'aria-label': item.serviceName ?? String(item.providerItemId) }}
                    data-testid={`med-nursing-item-${item.providerItemId}`}
                  />
                }
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{item.serviceName ?? item.serviceCode ?? '—'}</Typography>
                    {item.isReadOnly !== false ? (
                      <Typography variant="caption" color="text.secondary" data-testid={`med-nursing-readonly-${item.providerItemId}`}>
                        {t('med:nursing_cartable.already_actioned')}
                      </Typography>
                    ) : null}
                  </Stack>
                }
              />
            ))}

            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                disabled={selected.length === 0 || record.isPending || !canPerform}
                onClick={() => record.mutate(entry)}
                data-testid="med-nursing-record"
              >
                {t('med:nursing_cartable.record')}
              </Button>
            </Stack>
          </Stack>
        ))}

        {record.data ? (
          <Alert severity="success" data-testid="med-nursing-queued">
            {t('med:nursing_cartable.queued')}
          </Alert>
        ) : null}

        {record.error ? <ErrorState error={record.error} /> : null}
      </Stack>
    </ListPage>
  );
}
